import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { CheckCircle2, Calendar, MessageCircle, ArrowLeft, PoundSterling, Play, Image, Sparkles } from 'lucide-react';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
export function ServiceDetailPage() {
  const {
    serviceId
  } = useParams();
  const {
    t,
    lang,
    getArray
  } = useTranslation();
  const servicesData: Record<string, {
    image: string;
    seoKeywords?: string;
  }> = {
    fillings: {
      image: 'https://images.pexels.com/photos/3845766/pexels-photo-3845766.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'dental fillings london, white fillings angel, composite fillings islington' : lang === 'ua' ? 'пломбування зубів лондон, фотополімерні пломби, стоматолог angel' : 'пломбирование зубов лондон, фотополимерные пломбы, стоматолог angel'
    },
    'root-canal': {
      image: 'https://images.pexels.com/photos/3881449/pexels-photo-3881449.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'root canal london, endodontics angel, root canal treatment islington' : lang === 'ua' ? 'лікування каналів лондон, ендодонтія angel, перелікування каналів' : 'лечение каналов лондон, эндодонтия angel, перелечивание каналов'
    },
    crowns: {
      image: 'https://images.pexels.com/photos/3845625/pexels-photo-3845625.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'dental crowns london, ceramic crowns angel, crown fitting islington' : lang === 'ua' ? 'зубні коронки лондон, керамічні коронки angel, протезування' : 'зубные коронки лондон, керамические коронки angel, протезирование'
    },
    hygiene: {
      image: 'https://images.pexels.com/photos/3845653/pexels-photo-3845653.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'dental hygiene london, teeth cleaning angel, scale polish islington, airflow' : lang === 'ua' ? 'гігієна зубів лондон, чистка зубів angel, airflow, ультразвук' : 'гигиена зубов лондон, чистка зубов angel, airflow, ультразвук'
    },
    whitening: {
      image: 'https://images.pexels.com/photos/3762453/pexels-photo-3762453.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'teeth whitening london, professional whitening angel, zoom whitening islington' : lang === 'ua' ? 'відбілювання зубів лондон, професійне відбілювання angel' : 'отбеливание зубов лондон, профессиональное отбеливание angel'
    },
    extraction: {
      image: 'https://images.pexels.com/photos/3845810/pexels-photo-3845810.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'tooth extraction london, wisdom tooth removal angel, dental extraction islington' : lang === 'ua' ? 'видалення зубів лондон, видалення зуба мудрості angel' : 'удаление зубов лондон, удаление зуба мудрости angel'
    },
    'smile-makeover': {
      image: 'https://images.pexels.com/photos/3985163/pexels-photo-3985163.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'smile makeover london, veneers angel, cosmetic dentistry islington, composite bonding' : lang === 'ua' ? 'smile makeover лондон, вініри angel, естетична стоматологія, композитні накладки' : 'smile makeover лондон, виниры angel, эстетическая стоматология, композитные накладки'
    },
    cosmetic: {
      image: 'https://images.pexels.com/photos/3764013/pexels-photo-3764013.jpeg?auto=compress&cs=tinysrgb&w=800',
      seoKeywords: lang === 'en' ? 'cosmetic injections london, facial aesthetics angel, botox dentist islington' : lang === 'ua' ? "ін'єкційна косметологія лондон, естетика обличчя angel" : 'инъекционная косметология лондон, эстетика лица angel'
    }
  };
  const translationKey = serviceId?.replace(/-([a-z])/g, g => g[1].toUpperCase());
  if (!serviceId || !servicesData[serviceId]) {
    return <Navigate to={`/${lang}/services`} replace data-id="element-320" />;
  }
  const serviceImage = servicesData[serviceId].image;
  const title = t(`services.${translationKey}.title`);
  const description = t(`services.${translationKey}.fullDesc`);
  const benefits = getArray(`services.${translationKey}.benefits`);
  const isSmileMakeover = serviceId === 'smile-makeover';
  // Before/After gallery with real photos
  const beforeAfterCases = [{
    before: 'https://cdn.magicpatterns.com/uploads/shQ5ZRHdqwygEh9jr68EUz/IMG_8186.jpg',
    after: 'https://cdn.magicpatterns.com/uploads/pnzB7AsEyjzSLFCXJDe779/IMG_9004.jpg',
    description: lang === 'en' ? 'Smile transformation with veneers' : lang === 'ua' ? 'Трансформація посмішки вінірами' : 'Трансформация улыбки винирами'
  }, {
    before: 'https://cdn.magicpatterns.com/uploads/9Sh8YYorQi4Z3aQpsAEiEW/IMG_8183.jpg',
    after: 'https://cdn.magicpatterns.com/uploads/6FZYJz1gkBZaYnPMEjSFwB/IMG_9008.jpg',
    description: lang === 'en' ? 'Complete smile makeover' : lang === 'ua' ? 'Повна трансформація посмішки' : 'Полная трансформация улыбки'
  }];
  return <Layout data-id="element-321">
      {/* SEO Meta - would be handled by helmet in production */}

      {/* Hero Section */}
      <div className="bg-teal-900 py-16 text-white" data-id="element-322">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-323">
          <Link to={`/${lang}/services`} className="inline-flex items-center gap-2 text-teal-200 hover:text-white transition-colors mb-6" data-id="element-324">
            <ArrowLeft className="w-4 h-4" data-id="element-325" />
            {t('services.viewAll')}
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold" data-id="element-326">{title}</h1>
          {isSmileMakeover && <p className="mt-4 text-xl text-teal-100 max-w-3xl" data-id="element-327">
              {t('services.smileMakeover.seoDescription')}
            </p>}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-328">
        <div className="grid lg:grid-cols-2 gap-12 items-start" data-id="element-329">
          {/* Image Column */}
          <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] lg:sticky lg:top-24" data-id="element-330">
            <img src={serviceImage} alt={title} className="absolute inset-0 w-full h-full object-cover" data-id="element-331" />
          </div>

          {/* Content Column */}
          <div className="space-y-8" data-id="element-332">
            <div data-id="element-333">
              <p className="text-lg text-gray-600 leading-relaxed" data-id="element-334">
                {description}
              </p>
            </div>

            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100" data-id="element-335">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="element-336">
                {lang === 'en' ? 'Key Benefits' : lang === 'ua' ? 'Переваги' : 'Преимущества'}
              </h3>
              <ul className="space-y-4" data-id="element-337">
                {benefits.map((benefit, idx) => <li key={idx} className="flex items-start gap-3 text-gray-700" data-id="element-338">
                    <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" data-id="element-339" />
                    <span data-id="element-340">{benefit}</span>
                  </li>)}
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-341">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="flex-1" data-id="element-342">
                <Button size="lg" className="w-full gap-2" data-id="element-343">
                  <Calendar className="w-5 h-5" data-id="element-344" />
                  {t('servicesPage.bookOnlineNow')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex-1" data-id="element-345">
                <Button variant="outline" size="lg" className="w-full gap-2" data-id="element-346">
                  <MessageCircle className="w-5 h-5 text-green-600" data-id="element-347" />
                  {t('contact.whatsapp')}
                </Button>
              </a>
            </div>

            {/* Prices Link */}
            <Link to={`/${lang}/prices`} className="flex items-center gap-3 p-4 bg-teal-50 rounded-xl hover:bg-teal-100 transition-colors group" data-id="element-348">
              <div className="p-2 bg-teal-600 rounded-lg text-white" data-id="element-349">
                <PoundSterling className="w-5 h-5" data-id="element-350" />
              </div>
              <div className="flex-1" data-id="element-351">
                <p className="font-semibold text-gray-900 group-hover:text-teal-700 transition-colors" data-id="element-352">
                  {t('nav.prices')}
                </p>
                <p className="text-sm text-gray-600" data-id="element-353">
                  {lang === 'en' ? 'View our transparent pricing' : lang === 'ua' ? 'Переглянути наші ціни' : 'Посмотреть наши цены'}
                </p>
              </div>
              <ArrowLeft className="w-5 h-5 text-gray-400 rotate-180 group-hover:text-teal-600 transition-colors" data-id="element-354" />
            </Link>
          </div>
        </div>
      </div>

      {/* Smile Makeover Special Sections */}
      {isSmileMakeover && <>
          {/* Treatments Grid */}
          <div className="bg-white py-16" data-id="element-355">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-356">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-357">
                {lang === 'en' ? 'Our Smile Makeover Treatments' : lang === 'ua' ? 'Наші процедури Smile Makeover' : 'Наши процедуры Smile Makeover'}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-358">
                {[{
              icon: Sparkles,
              name: lang === 'en' ? 'Porcelain Veneers' : lang === 'ua' ? 'Керамічні вініри' : 'Керамические виниры',
              desc: lang === 'en' ? 'Ultra-thin ceramic shells for a perfect smile' : lang === 'ua' ? 'Ультратонкі керамічні накладки для ідеальної посмішки' : 'Ультратонкие керамические накладки для идеальной улыбки'
            }, {
              icon: Sparkles,
              name: lang === 'en' ? 'Composite Bonding' : lang === 'ua' ? 'Композитні накладки' : 'Композитные накладки',
              desc: lang === 'en' ? 'Tooth-colored resin to reshape and restore' : lang === 'ua' ? 'Реставрація зубів композитним матеріалом' : 'Реставрация зубов композитным материалом'
            }, {
              icon: Sparkles,
              name: lang === 'en' ? 'Teeth Whitening' : lang === 'ua' ? 'Відбілювання зубів' : 'Отбеливание зубов',
              desc: lang === 'en' ? 'Professional whitening for a brighter smile' : lang === 'ua' ? 'Професійне відбілювання для яскравої посмішки' : 'Профессиональное отбеливание для яркой улыбки'
            }, {
              icon: Sparkles,
              name: lang === 'en' ? 'Inlays & Onlays' : lang === 'ua' ? 'Вкладки та накладки' : 'Вкладки и накладки',
              desc: lang === 'en' ? 'Custom ceramic restorations for damaged teeth' : lang === 'ua' ? 'Індивідуальні керамічні реставрації' : 'Индивидуальные керамические реставрации'
            }, {
              icon: Sparkles,
              name: lang === 'en' ? 'Muscle Relaxation' : lang === 'ua' ? "Розслаблення м'язів" : 'Расслабление мышц',
              desc: lang === 'en' ? 'Facial aesthetics to complement your smile' : lang === 'ua' ? 'Естетичні процедури для обличчя' : 'Эстетические процедуры для лица'
            }, {
              icon: Sparkles,
              name: lang === 'en' ? '3D Scanning & Photo Protocol' : lang === 'ua' ? '3D-сканування та фотопротокол' : '3D-сканирование и фотопротокол',
              desc: lang === 'en' ? 'Digital planning for predictable results' : lang === 'ua' ? 'Цифрове планування для передбачуваного результату' : 'Цифровое планирование для предсказуемого результата'
            }].map((treatment, idx) => <div key={idx} className="bg-gray-50 p-6 rounded-xl border border-gray-100" data-id="element-359">
                    <treatment.icon className="w-8 h-8 text-teal-600 mb-4" data-id="element-360" />
                    <h3 className="font-semibold text-gray-900 mb-2" data-id="element-361">
                      {treatment.name}
                    </h3>
                    <p className="text-sm text-gray-600" data-id="element-362">{treatment.desc}</p>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Before & After Gallery */}
          <div className="bg-gray-50 py-16" data-id="element-363">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-364">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center" data-id="element-365">
                {t('services.smileMakeover.gallery.title')}
              </h2>
              <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto" data-id="element-366">
                {lang === 'en' ? "See the transformations we've achieved for our patients" : lang === 'ua' ? 'Подивіться на трансформації, яких ми досягли для наших пацієнтів' : 'Посмотрите на трансформации, которых мы достигли для наших пациентов'}
              </p>
              <div className="grid md:grid-cols-2 gap-8" data-id="element-367">
                {beforeAfterCases.map((caseItem, idx) => <div key={idx} className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200" data-id="element-368">
                    <div className="grid grid-cols-2" data-id="element-369">
                      <div className="relative aspect-square border-r border-gray-200" data-id="element-370">
                        <img src={caseItem.before} alt={`Before - Case ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" data-id="element-371" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3" data-id="element-372">
                          <span className="text-white text-sm font-medium uppercase tracking-wide" data-id="element-373">
                            {lang === 'en' ? 'Before' : lang === 'ua' ? 'До' : 'До'}
                          </span>
                        </div>
                      </div>
                      <div className="relative aspect-square" data-id="element-374">
                        <img src={caseItem.after} alt={`After - Case ${idx + 1}`} className="absolute inset-0 w-full h-full object-cover" data-id="element-375" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3" data-id="element-376">
                          <span className="text-white text-sm font-medium uppercase tracking-wide" data-id="element-377">
                            {lang === 'en' ? 'After' : lang === 'ua' ? 'Після' : 'После'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 text-center bg-teal-50" data-id="element-378">
                      <p className="text-sm font-medium text-teal-800" data-id="element-379">
                        {caseItem.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Video Section */}
          <div className="bg-white py-16" data-id="element-380">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-381">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center" data-id="element-382">
                {t('services.smileMakeover.video.title')}
              </h2>
              <div className="grid md:grid-cols-2 gap-8 mt-12" data-id="element-383">
                {[1, 2].map((_, idx) => <div key={idx} className="bg-gray-100 rounded-2xl aspect-video flex items-center justify-center border-2 border-dashed border-gray-300" data-id="element-384">
                    <div className="text-center" data-id="element-385">
                      <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-386">
                        <Play className="w-8 h-8 text-gray-400" data-id="element-387" />
                      </div>
                      <p className="text-gray-500" data-id="element-388">
                        {t('services.smileMakeover.video.placeholder')}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </>}

      {/* CTA Section */}
      <div className="bg-gray-50 py-20" data-id="element-389">
        <div className="max-w-3xl mx-auto px-4 text-center" data-id="element-390">
          <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-391">
            {t('servicesPage.readyToBook')}
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-392">
            {t('servicesPage.readyToBookDesc')}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-393">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-394">
              <Button size="lg" className="gap-2 w-full sm:w-auto" data-id="element-395">
                <Calendar className="w-5 h-5" data-id="element-396" />
                {t('servicesPage.bookOnlineNow')}
              </Button>
            </a>
            <Link to={`/${lang}/contact`} data-id="element-397">
              <Button variant="outline" size="lg" className="w-full sm:w-auto" data-id="element-398">
                {t('servicesPage.contactUs')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>;
}